import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ElementRef, SecurityContext } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { debounceTime, distinctUntilChanged, startWith, Subject, takeUntil } from 'rxjs';
import { TippyProps } from '@ngneat/helipopper/config';
import { MergeTagPickerConfig, MergeTagNode } from './merge-tag-picker.model';
import { buildTreeFromObject, mergeTagSearchOptionsFromConfig, searchMergeTags } from './merge-tag-picker.utils';

export const MERGE_TAG_PICKER_TIPPY_PROPS: Partial<TippyProps> = {
	trigger: 'manual',
	interactive: true,
	arrow: false,
	placement: 'bottom-start',
	maxWidth: 360,
	appendTo: 'parent',
	sticky: 'popper',
	offset: [0, 8],
	hideOnClick: false
};

const DEFAULT_CONFIG: Required<MergeTagPickerConfig> = {
	debounceMs: 200,
	minSearchLength: 2,
	searchPlaceholder: 'Search merge tags',
	noResultsLabel: 'No matches found',
	invalidDataLabel: 'Merge tag data unavailable',
	showExampleValues: true
};

@Component({
	selector: 'app-merge-tag-picker',
	templateUrl: './merge-tag-picker.component.html',
	styleUrls: ['./merge-tag-picker.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class MergeTagPickerComponent implements OnInit, OnChanges, OnDestroy {
	@Input() data: Record<string, unknown> | null = null;
	@Input() config: MergeTagPickerConfig | null = null;

	@Output() closed = new EventEmitter<void>();
	@Output() tagSelected = new EventEmitter<string>();
	@Output() invalid = new EventEmitter<string>();

	@ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

	readonly searchControl = new FormControl<string>('', { nonNullable: true });

	resolvedConfig: Required<MergeTagPickerConfig> = { ...DEFAULT_CONFIG };
	treeNodes: MergeTagNode[] = [];
	visibleTree: MergeTagNode[] = [];

	expandedPaths: Set<string> = new Set();
	defaultExpandedPaths: Set<string> = new Set();
	autoExpandedPaths: Set<string> = new Set();

	isSearchActive = false;
	noResults = false;
	isInvalidData = false;
	errorMessage: string | null = null;
	searchTerm: string = '';

	private readonly destroy$ = new Subject<void>();

	constructor(
		private readonly cdr: ChangeDetectorRef,
		private readonly sanitizer: DomSanitizer
	) {}

	ngOnInit(): void {
		this.setupSearchStream();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['config']) {
			this.resolvedConfig = { ...DEFAULT_CONFIG, ...(this.config ?? {}) };
			this.applySearch(this.searchControl.value ?? '');
		}

		if (changes['data']) {
			this.rebuildTree();
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	onClose(): void {
		this.closed.emit();
	}

	onToggleNode(path: string, expanded: boolean): void {
		if (expanded) {
			this.expandedPaths.add(path);
		} else {
			this.expandedPaths.delete(path);
		}
	}

	onSelectNode(node: MergeTagNode): void {
		if (!node.isLeaf) {
			return;
		}

		this.tagSelected.emit(`{{${node.path}}}`);
	}

	isPathExpanded(path: string): boolean {
		return this.expandedPaths.has(path);
	}

	private setupSearchStream(): void {
		this.searchControl.valueChanges
			.pipe(
				startWith(this.searchControl.value ?? ''),
				debounceTime(this.resolvedConfig.debounceMs),
				distinctUntilChanged(),
				takeUntil(this.destroy$)
			)
			.subscribe(value => this.applySearch(value ?? ''));
	}

	private rebuildTree(): void {
		if (!isValidDataSource(this.data)) {
			this.treeNodes = [];
			this.visibleTree = [];
			this.isInvalidData = true;
			this.errorMessage = this.resolvedConfig.invalidDataLabel;
			this.invalid.emit(this.errorMessage ?? 'Merge tag data unavailable');
			this.cdr.markForCheck();
			return;
		}

		this.treeNodes = buildTreeFromObject(this.data);
		this.defaultExpandedPaths = collectDefaultExpandedPaths(this.treeNodes);
		this.expandedPaths = new Set(this.defaultExpandedPaths);
		this.autoExpandedPaths.clear();
		this.isInvalidData = false;
		this.errorMessage = null;
		this.applySearch(this.searchControl.value ?? '');
	}

	private applySearch(term: string): void {
		this.searchTerm = term ?? '';

		if (!this.treeNodes.length) {
			this.visibleTree = [];
			this.noResults = !!this.searchTerm;
			this.cdr.markForCheck();
			return;
		}

		const normalizedTerm = this.searchTerm.trim();
		const searchResult = searchMergeTags(normalizedTerm, this.treeNodes, mergeTagSearchOptionsFromConfig(this.resolvedConfig));

		this.isSearchActive = normalizedTerm.length >= this.resolvedConfig.minSearchLength;
		this.noResults = this.isSearchActive && searchResult.filteredTree.length === 0;

		if (!this.isSearchActive) {
			this.expandedPaths = new Set(this.defaultExpandedPaths);
			this.autoExpandedPaths.clear();
		} else {
			this.autoExpandedPaths = new Set(searchResult.expandedPaths);
			this.expandedPaths = new Set([...this.defaultExpandedPaths, ...this.autoExpandedPaths, ...searchResult.matchedPaths]);
		}

		this.visibleTree = this.mapHighlights(searchResult.filteredTree, searchResult.highlights);
		this.cdr.markForCheck();
	}

	private mapHighlights(
		nodes: MergeTagNode[],
		highlightMap: Record<string, { label?: string; displayPath?: string }>
	): MergeTagNode[] {
		return nodes.map(node => {
			const highlight = highlightMap[node.path];
			const children = node.children ? this.mapHighlights(node.children, highlightMap) : undefined;

			return {
				...node,
				highlightedLabel: highlight?.label ? this.toSafeHtml(highlight.label) : undefined,
				highlightedPath: highlight?.displayPath ? this.toSafeHtml(highlight.displayPath) : undefined,
				children
			};
		});
	}

	focusSearchInput(): void {
		if (typeof window === 'undefined') {
			return;
		}

		setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
	}

	private toSafeHtml(value: string): SafeHtml | null {
		// SECURITY: Use sanitize() instead of bypassSecurityTrustHtml()
		// The input is already HTML-escaped by highlightText() function,
		// but we use sanitize() for defense-in-depth
		return this.sanitizer.sanitize(SecurityContext.HTML, value);
	}
}

function isValidDataSource(value: unknown): value is Record<string, unknown> {
	return !!value && typeof value === 'object';
}

function collectDefaultExpandedPaths(_nodes: MergeTagNode[]): Set<string> {
	return new Set<string>();
}
